import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BiSolidCarBattery } from 'react-icons/bi';
import { MdMonitorHeart } from 'react-icons/md';
import { RiLayoutMasonryFill } from 'react-icons/ri';
import { MdPeopleAlt } from 'react-icons/md';
import { FaUsersCog } from 'react-icons/fa';

import logo from '../../assets/images/logoW.svg';
import logoM from '../../assets/images/logoB.svg';
import {
  NoticePageUrl,
  StuffPageUrl,
  DiagnosisPageUrl,
  AdminPageUrl,
  MyPageUrl,
  LoginPageUrl,
} from '../../url/poenUrl';
import AuthService from '../../api/service/auth/AuthService';

const SidebarContainer = styled.div<{ $isOpen: boolean }>`
  position: relative;
  width: 240px;
  background-color: ${(props) => props.theme.colors.mainColor};
  transition: transform 0.3s ease;
  z-index: 1000;
  transform: ${({ $isOpen }) =>
    $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  box-shadow: 5px 0 15px rgba(0, 0, 0, 0.1);

  @media (min-width: 969px) {
    transform: translateX(0);
  }
  @media (max-width: 968px) {
    background-color: ${(props) => props.theme.colors.whiteColor};
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  @media (min-width: 969px) {
    display: none;
  }
`;

const CloseButton = styled.button`
  display: none;
  @media (max-width: 968px) {
    display: block;
    position: absolute;
    top: 10px;
    right: 20px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
  }
`;

const Logo = styled.img<{ $isMobile: boolean }>`
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  content: ${(props) => (props.$isMobile ? `url(${logoM})` : `url(${logo})`)};
  @media (max-width: 968px) {
    top: 60px;
    width: 100px;
  }
`;

const SidebarBox = styled.div`
  height: 100vh;
  width: 100%;
  gap: 30px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  @media (max-width: 968px) {
    padding: 0px;
    gap: 5px;
    justify-content: end;
  }
`;

const Menu = styled.ul`
  display: flex;
  align-items: center;
  padding: 0px 20px;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  @media (max-width: 968px) {
    background-color: ${(props) => props.theme.colors.mainColor};
    height: calc(100vh / 5); // ✨ 모바일에서도 동일한 높이 적용
    gap: 5px;
    padding: 20px;
    align-items: center;
    justify-content: center;
  }
`;

const MenuItemBase = styled(NavLink)`
  width: 100%;
  display: flex;
  height: 76px;
  align-items: center;
  gap: 10px; // ✨ 아이콘과 텍스트 사이의 간격
  border-radius: 10px;
  padding: 0px 20px;
  text-decoration: none;
  color: ${(props) => props.theme.colors.whiteColor};

  span {
    font-size: 22px;
    font-weight: 600;
  }

  svg {
    font-size: 36px; // ✨ 아이콘 크기 키우기
  }

  &.active {
    background-color: ${(props) => props.theme.colors.activeColor};
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.hoverColor};
  }

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const LogoutBotton = styled.button`
  display: none;
  @media (max-width: 968px) {
    position: absolute;
    top: 100px;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    padding: 5px 10px;
    font-size: 12px;
    color: ${(props) => props.theme.colors.textColor};
    background-color: ${(props) => props.theme.colors.grayColor};
    border: none;
    border-radius: 5px;
    &:hover {
      font-weight: 700;
    }
  }
`;

const Sidebar: React.FC<{ isOpen: boolean; toggleSidebar: () => void }> = ({
  isOpen,
  toggleSidebar,
}) => {
  const navigate = useNavigate(); // 리디렉션을 위한 useNavigate 훅 사용
  const isMobile = window.innerWidth <= 968; // ✨ 모바일 환경 감지
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    const storedPermissions = localStorage.getItem('permissions');

    if (storedPermissions === 'ADMIN') {
      setIsAdmin(true);
    }
  }, []);

  const renderMenuItem = (to: string, text: string, icon: React.ReactNode) => {
    const location = useLocation();

    return (
      <MenuItemBase
        to={to}
        className={({ isActive }) => {
          const isActiveLink = isActive || location.pathname.includes(to);
          return isActiveLink ? 'active' : '';
        }}
      >
        {icon}
        <span>{text}</span>
      </MenuItemBase>
    );
  };

  const handleLogout = async () => {
    try {
      const response = await AuthService.logout(); // AuthService의 logout 메서드 호출
      alert(response.resultMsg);
      navigate(LoginPageUrl); // 로그아웃 후 로그인 페이지로 리디렉션
    } catch (error) {
      alert(error);
      console.error('로그아웃 실패:', error);
    }
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={toggleSidebar} />
      <SidebarContainer $isOpen={isOpen}>
        <CloseButton onClick={toggleSidebar}>✕</CloseButton>
        <Logo src={logo} $isMobile={isMobile} alt="로고" />{' '}
        <LogoutBotton onClick={handleLogout}>로그아웃</LogoutBotton>
        {/* ✨ 모바일 여부 전달 */}
        <SidebarBox>
          <Menu>
            {renderMenuItem(
              `${StuffPageUrl}?activeTab=0`,
              '매물 정보',
              <BiSolidCarBattery />
            )}
          </Menu>
          <Menu>
            {renderMenuItem(
              `${DiagnosisPageUrl}?activeTab=0`,
              '진단 정보',
              <MdMonitorHeart />
            )}
          </Menu>
          <Menu>
            {renderMenuItem(
              `${NoticePageUrl}?activeTab=0`,
              '게시판',
              <RiLayoutMasonryFill />
            )}
          </Menu>

          {isAdmin ? (
            <Menu>
              {renderMenuItem(
                `${AdminPageUrl}?activeTab=0`,
                '관리자',
                <FaUsersCog />
              )}
            </Menu>
          ) : (
            <Menu>
              {renderMenuItem(
                `${MyPageUrl}?activeTab=0`,
                '마이페이지',
                <MdPeopleAlt />
              )}
            </Menu>
          )}
        </SidebarBox>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
